import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import './ContactSection.css';

const INQUIRY_TYPES = [
  { value: 'support', label: 'Plugin Support' },
  { value: 'coaching', label: 'Coaching Inquiry' },
];

function ContactSection() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    inquiry_type: 'support',
    message: '',
    // Honeypot — real users never fill this. See the hidden input below and
    // the matching silent-200 check in lambda/api/handlers/inquiries.ts
    // (cross-port from donationDefender).
    _t: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await api.post('/inquiries', form);
      setSubmitted(true);
      setForm({ name: '', email: '', inquiry_type: 'support', message: '', _t: '' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container contact-content">
        <h2 className="contact-title">Get in Touch</h2>
        <p className="contact-subtitle">
          Questions about FLUX? Interested in coaching sessions? We'd love to hear from you.
        </p>

        {submitted ? (
          <div className="contact-success">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Message Sent</h3>
            <p>Thanks for reaching out. We'll get back to you soon.</p>
            <button className="contact-reset" onClick={() => setSubmitted(false)}>
              Send Another Message
            </button>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit}>
            {/*
              Honeypot: unreachable to keyboard / screen reader / password
              manager, but naive bots fill every input and trip the silent-201
              in the backend handler. Inline style keeps the component
              self-contained — no CSS file change needed.
            */}
            <input
              type="text"
              name="_t"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form._t}
              onChange={handleChange}
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                opacity: 0,
                pointerEvents: 'none',
              }}
            />

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact-name">Name</label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label>What can we help with?</label>
              <div className="type-selector">
                {INQUIRY_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    className={`type-btn ${form.inquiry_type === type.value ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, inquiry_type: type.value })}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="contact-message">Message</label>
              <textarea
                id="contact-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                rows={5}
                placeholder={form.inquiry_type === 'coaching'
                  ? 'Tell us about your experience level, gear, and what you want to learn...'
                  : 'Describe your issue or question...'}
              />
            </div>

            {error && <p className="form-error">{error}</p>}

            <button type="submit" className="contact-submit" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export default ContactSection;
